import { Fetcher, FetcherInit } from '@/adapters/fetcher';
import { newVersionMiddleware } from '@/adapters/newVersionMiddleware';

class PublicESignFetcher extends Fetcher {
  constructor(options = {} as Partial<FetcherInit>) {
    super({
      ...options,
      // Intentionally no x-fe-page middleware: window.location.href on this
      // route contains the signer's bearer token, which must never reach
      // backend access logs.
      beforeInit: [
        async (_input, init) => {
          // Only set Content-Type for JSON bodies; leave FormData /
          // URLSearchParams boundaries alone. Header names are
          // case-insensitive per RFC 7230.
          const hasContentType = Object.keys(init.headers).some(
            (key) => key.toLowerCase() === 'content-type'
          );
          if (init.json && !hasContentType) {
            init.headers['Content-Type'] = 'application/json';
          }
        },
        ...(options.beforeInit ?? [])
      ]
    });

    const hooks = this.options.hooks ?? {};

    // Intentionally REPLACE (not append to) the base Fetcher afterResponse
    // chain here. The default chain includes createGlobalErrorHandler(),
    // which redirects 401/403 responses to /unauthorized and clears Auth0
    // localStorage — correct for authenticated routes, wrong for an
    // unauthenticated public signer whose errors are modeled by
    // normalizePublicSignerError (invalid/expired/superseded/...). Keeping
    // newVersionMiddleware is required so a stale signer session still
    // gets prompted to reload on a new frontend deploy.
    this.options.hooks = {
      ...hooks,
      afterResponse: [
        async (_input, _options, response) => newVersionMiddleware(response)
      ]
    };
  }

  async json<T = Record<string, any>>(
    input: string,
    options = {} as Partial<FetcherInit>
  ) {
    const json = await super.json(input, options);
    // `'data' in json` throws TypeError if the BE ever returns `null` or a
    // primitive string. Narrow to a non-null object first.
    if (json && typeof json === 'object' && 'data' in json) {
      return (json as { data: unknown }).data as T;
    }
    return json as T;
  }

  get<T = Record<string, any>>(
    input: string,
    options = {} as Partial<FetcherInit>
  ) {
    return this.json<T>(input, { ...options, method: 'GET' });
  }

  post<T = Record<string, any>>(
    input: string,
    json = {} as FetcherInit['json'],
    options = {} as Partial<FetcherInit>
  ) {
    return this.json<T>(input, { ...options, method: 'POST', json });
  }
}

const publicESignFetch = new PublicESignFetcher({
  prefixUrl: import.meta.env.VITE_API_BASE_PATH_ESIGN
});

export default publicESignFetch;
